/**
 * BulkDeleteDialog - Dialog de confirmação de exclusão em massa genérico
 * Reutilizável em todas as telas CRUD
 *
 * Props:
 * - open: boolean
 * - onClose: function
 * - onConfirm: function
 * - selectedCount: number
 * - loading: boolean
 * - entityName: string (ex: 'motorista(s)', 'grupo(s)', 'usuário(s)')
 * - title: string (opcional, título customizado)
 * - message: string (opcional, mensagem customizada)
 */

import React, { memo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
  Box,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { usePremiumTokens } from '../../theme/usePremiumTokens';
import { useTranslation } from '../LocalizationProvider';

const BulkDeleteDialog = memo(({
  open,
  onClose,
  onConfirm,
  selectedCount,
  loading = false,
  entityName = 'item(s)',
  title,
  message,
}) => {
  const t = useTranslation();
  const tokens = usePremiumTokens();

  // Cores com fallback
  const colors = {
    surface: tokens?.surface?.elevated || '#161b22',
    border: tokens?.border?.subtle || 'rgba(255,255,255,0.1)',
    textPrimary: tokens?.text?.primary || '#f0f6fc',
    textSecondary: tokens?.text?.secondary || '#8b949e',
    error: tokens?.semantic?.error || '#f85149',
    errorHover: '#da3633',
    hover: tokens?.surface?.hover || 'rgba(255,255,255,0.05)',
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const displayTitle = title || t('sharedConfirmDelete') || 'Confirmar Exclusão';
  const displayMessage = message || `Tem certeza que deseja excluir ${selectedCount} ${entityName}?`;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={loading}
      disableEnforceFocus
      disableAutoFocus
      keepMounted={false}
      PaperProps={{
        sx: {
          bgcolor: colors.surface,
          borderRadius: tokens?.radius?.lg || '12px',
          border: `1px solid ${colors.border}`,
          backgroundImage: 'none',
          m: 2,
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
        },
      }}
    >
      <DialogTitle
        sx={{
          color: colors.textPrimary,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          pb: 1,
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: `${colors.error}20`,
          }}
        >
          <WarningAmberIcon sx={{ color: colors.error }} />
        </Box>
        {displayTitle}
      </DialogTitle>

      <DialogContent>
        <DialogContentText sx={{ color: colors.textSecondary }}>
          {displayMessage}
          <br />
          <strong style={{ color: colors.error }}>
            {t('sharedActionCannotBeUndone') || 'Esta ação não pode ser desfeita.'}
          </strong>
        </DialogContentText>
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 1, gap: 1 }}>
        <Button
          onClick={handleClose}
          disabled={loading}
          sx={{
            color: colors.textSecondary,
            '&:hover': {
              backgroundColor: colors.hover,
            },
          }}
        >
          {t('sharedCancel') || 'Cancelar'}
        </Button>
        <Button
          onClick={onConfirm}
          disabled={loading}
          variant="contained"
          startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <DeleteIcon />}
          sx={{
            backgroundColor: colors.error,
            color: '#ffffff',
            minWidth: 120,
            '&:hover': {
              backgroundColor: colors.errorHover,
            },
            '&:disabled': {
              backgroundColor: colors.error,
              opacity: 0.7,
            },
          }}
        >
          {loading
            ? (t('sharedDeleting') || 'Excluindo...')
            : (t('sharedRemove') || 'Excluir')}
        </Button>
      </DialogActions>
    </Dialog>
  );
});

BulkDeleteDialog.displayName = 'BulkDeleteDialog';

export default BulkDeleteDialog;
